import React from 'react';
import { motion } from 'framer-motion';
import { CTAButton } from './CTAButton';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 24
  },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      delay: i * 0.12,
      ease: [0.25, 0.1, 0.25, 1]
    }
  })
};
const floatAnimation = {
  y: [-4, 4, -4],
  transition: {
    duration: 5,
    repeat: Infinity,
    ease: 'easeInOut'
  }
};
export function HeroSection() {
  return <section className="w-full bg-deep pt-10 pb-10 md:pt-10 md:pb-10 px-6" data-id="element-159">
      <div className="max-w-2xl mx-auto" data-id="element-160">
        {/* Headline */}
        <motion.div className="text-center mb-16" initial="hidden" animate="visible" data-id="element-161">
          <motion.p className="text-cream-muted font-body text-sm tracking-[0.2em] uppercase mb-6" variants={fadeUp} custom={0} data-id="element-162">
            For Women 45+
          </motion.p>
          <motion.h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-cream leading-tight mb-4" variants={fadeUp} custom={1} data-id="element-163">
            The 3AM Reset
          </motion.h1>
          <motion.p className="font-heading text-xl md:text-2xl text-amber font-medium italic" variants={fadeUp} custom={2} data-id="element-164">
            What To Do When You Wake Up Wired
          </motion.p>
        </motion.div>

        {/* Ebook Cover */}
        <motion.div className="flex justify-center mb-10" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.8,
        delay: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }} data-id="element-165">
          <motion.div animate={floatAnimation} className="relative" data-id="element-166">
            <div className="absolute -inset-4 bg-teal/10 rounded-2xl blur-2xl" data-id="element-167" />
            <div className="absolute -inset-8 bg-amber/5 rounded-3xl blur-3xl" data-id="element-168" />
            <img src="https://cdn.magicpatterns.com/uploads/eGLhRJVnmwdgSCn6xgXrdU/ebookcover1.jpg" alt="The 3AM Reset ebook displayed on a tablet on a nightstand" className="relative w-72 md:w-80 rounded-lg shadow-2xl shadow-black/40" data-id="element-169" />
          </motion.div>
        </motion.div>

        {/* Opening Hook */}
        <motion.div className="space-y-2" initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-30px'
      }} data-id="element-170">
          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={0} data-id="element-171">
            It's not that you can't sleep.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={1} data-id="element-172">
            You <em className="text-cream italic" data-id="element-173">can</em> sleep.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={2} data-id="element-174">
            You fall asleep… and for a while everything is fine.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-amber font-medium leading-loose-plus" variants={fadeUp} custom={3} data-id="element-175">
            Then it happens.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={4} data-id="element-176">
            You open your eyes and you already know what time it is — because
            your body has that specific feeling:
          </motion.p>

          <motion.p className="font-heading text-xl md:text-2xl text-cream-muted italic leading-relaxed-plus pl-4 border-l-2 border-teal/30" variants={fadeUp} custom={5} data-id="element-177">
            tired… but somehow <span className="text-amber" data-id="element-178">on</span>.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={6} data-id="element-179">
            And your brain does the worst thing possible.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={7} data-id="element-180">
            It starts talking.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream-muted leading-loose-plus" variants={fadeUp} custom={8} data-id="element-181">
            Not in a helpful way. In a{' '}
            <em data-id="element-182">
              "let's review your entire life, your responsibilities, your
              health, your to-do list, that awkward conversation, and tomorrow
              morning"
            </em>{' '}
            way.
          </motion.p>

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={9} data-id="element-183">
            And once you check the clock, it's like your nervous system takes it
            personally.
          </motion.p>

          <motion.div className="h-px w-16 bg-teal/30 mx-auto my-10" variants={fadeUp} custom={10} data-id="element-184" />

          <motion.p className="font-body text-lg md:text-xl text-cream leading-loose-plus" variants={fadeUp} custom={11} data-id="element-185">
            If this is you, you don't need another list of sleep tips.
          </motion.p>

          <motion.p className="font-heading text-xl md:text-2xl text-cream font-semibold leading-relaxed-plus" variants={fadeUp} custom={12} data-id="element-186">
            You need one thing:{' '}
            <span className="text-teal-light" data-id="element-187">a default response</span> for the
            moment you wake up.
          </motion.p>

          {/* First CTA */}
          <motion.div className="pt-10 flex justify-center" variants={fadeUp} custom={13} data-id="element-188">
            <CTAButton href="#pricing" data-id="element-189">Download The 3AM Reset</CTAButton>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}